/**
 * sb-geo.js — country suggestion for visitors browsing from another market
 * ---------------------------------------------------------------------------
 * Shopify retired its standalone Geolocation app, so this is the theme's own.
 *
 * IT SUGGESTS, IT NEVER SWITCHES. A visitor's IP country is not their delivery
 * country — a UK customer ordering a replacement bud while on holiday in Spain
 * must not be moved to EUR and EU shipping behind their back. Silently changing
 * a displayed price also reads as a fault rather than a feature, and hard IP
 * redirects are the one pattern Shopify warns against for SEO, because Google
 * crawls mostly from US addresses.
 *
 * WHERE THE DATA COMES FROM
 *   /browsing_context_suggestions.json is Shopify's own endpoint and needs no
 *   app. Verified on this store: it returns
 *     { detected_values: { country: { handle, name } }, suggestions: [...] }
 *   with `suggestions` empty when the visitor is already in the right market —
 *   which is why the detected country is compared against the current one here
 *   rather than trusting that array's shape.
 *
 * NO COUNTRY LIST IS DUPLICATED. Everything needed — the available countries,
 * their names, their currency symbols and the means to switch — already exists
 * in the header's localization form. This reads that form and, on accept,
 * clicks its button, so the switch goes through the same Shopify-generated
 * form as the manual selector. One source of truth, one code path.
 *
 * Vanilla, no dependencies.
 */
(() => {
  const STORE_KEY = 'sb-geo-choice';

  const form = document.getElementById('sb-c-localization');
  const card = document.querySelector('[data-sb-geo]');
  /* The FORM is the hard requirement — the selector reordering below depends on
     it and runs for every visitor. The card is optional: without it there is
     simply nothing to suggest, and the ordering still happens. */
  if (!form) return;

  /* Already answered — for this visitor the question of SWITCHING is settled,
     whichever way they answered it. Stored rather than session-only so it
     survives the tab.

     This suppresses the card ONLY. The detected country is still promoted in
     the selector, because "put it near the top" is about the list being easy to
     use, not about asking again — and re-asking someone who already said no is
     the thing this key exists to prevent. */
  let answered = false;
  try {
    answered = !!localStorage.getItem(STORE_KEY);
  } catch (err) {
    /* Storage blocked (private mode, cookies off). Treat as answered: the
       ordering still applies, but no card, rather than one that reappears on
       every page because nothing can be written. */
    answered = true;
  }
  const canSuggest = !!card && !answered;

  const remember = (value) => {
    try {
      localStorage.setItem(STORE_KEY, value);
    } catch (err) {
      /* noop — the card is already closing either way */
    }
  };

  const rows = [...form.querySelectorAll('button[name="country_code"]')];
  const currentRow = form.querySelector('button[aria-current="true"]');
  const currentCode = currentRow?.value || null;

  /* ── Keeping clear of the cookie card ──────────────────────────────────
     Both notices live in the bottom-left corner, and consent is the lower of
     the two. Rather than hardcoding a height — which the consent copy, the
     locale or a narrow viewport would all invalidate — the card's real height
     is measured and published as --sb-geo-lift for the CSS to sit above.

     Re-measured on three signals: the cookie card being answered (its data-on
     attribute), its box changing, and the viewport resizing. When it is gone
     the lift returns to 0 and this card drops into the corner itself. */
  const cookie = document.querySelector('.sb-ck');
  const GAP = 12;

  const syncLift = () => {
    const open = cookie && !cookie.hidden && cookie.hasAttribute('data-on');
    const lift = open ? Math.round(cookie.getBoundingClientRect().height) + GAP : 0;
    card.style.setProperty('--sb-geo-lift', `${lift}px`);
  };

  const watchCookie = () => {
    if (!cookie) return;
    new MutationObserver(syncLift).observe(cookie, {
      attributes: true,
      attributeFilter: ['data-on', 'hidden'],
    });
    if (window.ResizeObserver) new ResizeObserver(syncLift).observe(cookie);
    addEventListener('resize', syncLift, { passive: true });
  };

  const show = (row, name, currency) => {
    syncLift();
    watchCookie();
    card.querySelector('[data-sb-geo-country]').textContent = name;
    const cur = card.querySelector('[data-sb-geo-currency]');
    if (currency) cur.textContent = currency;
    else cur.remove();

    const flag = row.querySelector('.sb-c-country__flag');
    const slot = card.querySelector('[data-sb-geo-flag]');
    if (flag && slot) slot.style.backgroundImage = getComputedStyle(flag).backgroundImage;

    card.hidden = false;
    /* Next frame, so the transition has a state to run FROM. Setting the
       attribute in the same frame as hidden=false gives no transition at all. */
    requestAnimationFrame(() => card.setAttribute('data-on', ''));

    card.querySelector('[data-sb-geo-accept]').addEventListener('click', () => {
      remember(row.value);
      /* Submits the header's own localization form, which posts to
         /localization with the right hidden fields. */
      row.click();
    });
  };

  const dismiss = () => {
    remember(currentCode || 'stay');
    card.removeAttribute('data-on');
    const done = () => { card.hidden = true; };
    /* Wait for the fade rather than guessing at a timeout; the fallback covers
       reduced-motion, where the transition may never fire an event. */
    card.addEventListener('transitionend', done, { once: true });
    setTimeout(done, 500);
  };

  if (card) {
    card.querySelector('[data-sb-geo-dismiss]').addEventListener('click', dismiss);
    card.querySelector('[data-sb-geo-stay]').addEventListener('click', dismiss);
  }

  /* ── Detected country sits second in the selector ───────────────────────
     Owner 2026-08-05: the country we think they are in should be near the top
     of the list. The SELECTED one is already first — sections/sb-header.liquid
     renders it ahead of the rest — so the detected one is promoted to directly
     below it, which is the "Austria selected, United Kingdom right under it"
     the owner described.

     Done here rather than in Liquid because only the client knows the detected
     country; the template has no access to the visitor's IP. It runs whether or
     not the suggestion card is shown, so the ordering is right even when the
     two countries match and there is nothing to suggest. */
  const promote = (row) => {
    if (!row || row.hasAttribute('aria-current')) return; /* already first */
    const first = form.querySelector('button[name="country_code"]');
    if (!first || first === row) return;
    first.after(row);
  };

  fetch('/browsing_context_suggestions.json', { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      const detected = data?.detected_values?.country;
      if (!detected?.handle) return;

      /* Only offer a country this store actually sells to — the endpoint
         reports where the visitor IS, not where we ship. */
      const row = rows.find((b) => b.value === detected.handle);
      if (!row) return;

      promote(row);
      if (!canSuggest) return; /* ordering only — already answered, or no card */
      if (currentCode && detected.handle === currentCode) return; /* already right */

      const name = row.querySelector('.sb-c-country-panel__name')?.textContent.trim() || detected.name;
      const currency = row.querySelector('.sb-c-country-panel__cur')?.textContent.trim() || '';
      show(row, name, currency);
    })
    .catch(() => {
      /* Offline or endpoint unavailable — no card. The manual selector in the
         header is unaffected and remains the way to change country. */
    });
})();
